<script>
  import { authState } from "../stores/auth.js";
  import { licenseState, isTrial } from "../stores/license.js";
  import { scanState } from "../stores/scan.js";
  import { localDBState, refreshLocalCounts } from "../stores/localdb.js";
  import { appStatus } from "../stores/ui.js";
  import { onMount } from "svelte";
  import { t } from "svelte-i18n";

  onMount(() => {
    appStatus.set({
      primary: "Ready for onboarding",
      secondary: "Complete login + passphrase to scan"
    });
    refreshLocalCounts();
  });

  const highlights = [
    { labelKey: "overview.highlights.vaultLabel", valueKey: "overview.highlights.vaultValue" },
    { labelKey: "overview.highlights.storageLabel", valueKey: "overview.highlights.storageValue" },
    { labelKey: "overview.highlights.apiLabel", valueKey: "overview.highlights.apiValue" },
    { labelKey: "overview.highlights.licenseLabel", valueKey: "overview.highlights.licenseValue" }
  ];
</script>

<section class="hero">
  <div>
    <p class="eyebrow">{$t("overview.eyebrow")}</p>
    <h1>{$t("overview.headline")}</h1>
    <p class="lead">{$t("overview.lead")}</p>
    <div class="cta-row">
      <button class="primary">{$t("overview.ctaOnboarding")}</button>
      <button class="secondary">{$t("overview.ctaImport")}</button>
    </div>
  </div>
  <div class="hero-card">
    <div class="hero-grid">
      {#each highlights as item}
        <div>
          <div class="metric-label">{$t(item.labelKey)}</div>
          <div class="metric-value">{$t(item.valueKey)}</div>
        </div>
      {/each}
    </div>
  </div>
</section>

<section class="grid">
  <div class="panel">
    <div class="panel-title">{$t("overview.accountSnapshot")}</div>
    <div class="panel-row">
      <span>{$t("overview.signedIn")}</span>
      <strong>{ $authState.isAuthenticated ? $t("common.yes") : $t("common.no") }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.plan")}</span>
      <strong>{ $licenseState.plan }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.trialEnds")}</span>
      <strong>{ $licenseState.trialEndsAt }</strong>
    </div>
    {#if $isTrial}
      <div class="panel-note">{$t("overview.trialNote")}</div>
    {/if}
  </div>

  <div class="panel">
    <div class="panel-title">{$t("overview.localArchive")}</div>
    <div class="panel-row">
      <span>{$t("overview.channelsStored")}</span>
      <strong>{ $localDBState.channelCount }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.videosStored")}</span>
      <strong>{ $localDBState.videoCount }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.lastImport")}</span>
      <strong>{ $localDBState.lastImportedAt || $t("overview.notYet") }</strong>
    </div>
    <button class="secondary">{$t("overview.openImportWizard")}</button>
  </div>

  <div class="panel wide">
    <div class="panel-title">{$t("overview.scanStatus")}</div>
    <div class="panel-row">
      <span>{$t("common.status")}</span>
      <strong>{ $scanState.status }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.quotaRemaining")}</span>
      <strong>{ $scanState.quotaRemaining }</strong>
    </div>
    <div class="panel-row">
      <span>{$t("overview.lastScan")}</span>
      <strong>{ $scanState.lastScanAt || $t("overview.never") }</strong>
    </div>
    <div class="list">
      <div class="list-title">{$t("overview.latestEvents")}</div>
      {#if $scanState.recentEvents.length === 0}
        <div class="empty">{$t("overview.noEvents")}</div>
      {:else}
        {#each $scanState.recentEvents as event}
          <div class="list-item">{event.message}</div>
        {/each}
      {/if}
    </div>
  </div>
</section>

<style>
  .hero {
    display: grid;
    grid-template-columns: 1.2fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;
    align-items: center;
  }

  .eyebrow {
    font-size: 0.75rem;
    letter-spacing: 0.2em;
    text-transform: uppercase;
    color: var(--muted);
  }

  h1 {
    font-size: clamp(2rem, 3vw, 2.8rem);
    margin: 0.5rem 0 1rem;
  }

  .lead {
    color: var(--muted);
    font-size: 1.05rem;
  }

  .cta-row {
    display: flex;
    gap: 1rem;
    margin-top: 1.5rem;
  }

  .primary,
  .secondary {
    padding: 0.7rem 1.2rem;
    border-radius: 999px;
    border: 1px solid var(--line);
    cursor: pointer;
    font-weight: 600;
  }

  .primary {
    background: var(--accent);
    color: #fff;
    border-color: transparent;
  }

  .secondary {
    background: rgba(255, 255, 255, 0.06);
    color: var(--ink);
  }

  .hero-card {
    background: linear-gradient(140deg, rgba(96, 165, 250, 0.18), rgba(167, 139, 250, 0.16));
    border-radius: 24px;
    padding: 1.75rem;
    border: 1px solid var(--line);
    box-shadow: var(--shadow);
  }

  .hero-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
  }

  .metric-label {
    font-size: 0.75rem;
    color: var(--muted);
    text-transform: uppercase;
    letter-spacing: 0.12em;
  }

  .metric-value {
    font-size: 1.1rem;
    font-weight: 600;
  }

  .grid {
    display: grid;
    gap: 1.5rem;
    grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  }

  .panel {
    background: rgba(255, 255, 255, 0.04);
    border-radius: 18px;
    padding: 1.5rem;
    border: 1px solid var(--line);
    display: grid;
    gap: 0.75rem;
  }

  .panel.wide {
    grid-column: 1 / -1;
  }

  .panel-title {
    font-weight: 700;
  }

  .panel-row {
    display: flex;
    justify-content: space-between;
    color: var(--muted);
  }

  .panel-note {
    background: rgba(96, 165, 250, 0.14);
    padding: 0.5rem 0.75rem;
    border-radius: 12px;
    font-size: 0.85rem;
  }

  .list {
    margin-top: 0.5rem;
  }

  .list-title {
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .list-item {
    padding: 0.4rem 0;
    border-bottom: 1px dashed rgba(148, 163, 184, 0.22);
    font-size: 0.9rem;
  }

  .empty {
    color: var(--muted);
    font-size: 0.9rem;
  }

  @media (max-width: 900px) {
    .hero {
      grid-template-columns: 1fr;
    }
  }
</style>
